import { tracked } from '@glimmer/tracking';
import type { LineRange } from 'codecrafters-frontend/components/code-mirror';
import parseDiffAsDocument from 'codecrafters-frontend/utils/parse-diff-as-document';
import { LineData, LineDataCollection } from 'codecrafters-frontend/utils/code-mirror-line-comments';

function generateRandomLineData(linesCount = 0) {
  function getRandomInt(inclusiveMin: number, exclusiveMax: number) {
    const minCeiled = Math.ceil(inclusiveMin);
    const maxFloored = Math.floor(exclusiveMax);

    return Math.floor(Math.random() * (maxFloored - minCeiled) + minCeiled);
  }

  const lineData = Array.from({ length: linesCount }).map(function (_v, lineNumber) {
    const rnd = Math.random();

    let commentsCount;

    if (rnd < 0.05) {
      commentsCount = getRandomInt(100, 1000);
    } else if (rnd < 0.1) {
      commentsCount = getRandomInt(10, 100);
    } else if (rnd < 0.8) {
      commentsCount = 0;
    } else {
      commentsCount = getRandomInt(1, 10);
    }

    return new LineData(lineNumber + 1, commentsCount);
  });

  return new LineDataCollection(lineData);
}

export class ExampleDocument {
  @tracked document: string = '';
  @tracked originalDocument: string;
  @tracked filename: string;
  @tracked language: string;
  @tracked highlightedRanges: LineRange[];
  @tracked collapsedRanges: LineRange[];
  @tracked lineData?: LineDataCollection;

  constructor({
    document = '',
    originalDocument,
    filename,
    language,
    highlightedRanges = [],
    collapsedRanges = [],
  }: {
    document?: string;
    originalDocument?: string;
    filename: string;
    language: string;
    highlightedRanges?: LineRange[];
    collapsedRanges?: LineRange[];
  }) {
    this.document = document;
    this.originalDocument = originalDocument || document;
    this.filename = filename;
    this.language = language;
    this.highlightedRanges = highlightedRanges;
    this.collapsedRanges = collapsedRanges;

    this.lineData = generateRandomLineData(document?.split('\n').length);
  }

  static createEmpty() {
    return new this({ filename: 'empty.txt', language: 'text' });
  }
}

export class DiffBasedExampleDocument extends ExampleDocument {
  @tracked diff?: string;

  constructor({
    diff,
    filename,
    language,
    highlightedRanges = [],
    collapsedRanges = [],
  }: {
    diff?: string;
    filename: string;
    language: string;
    highlightedRanges?: LineRange[];
    collapsedRanges?: LineRange[];
  }) {
    const { current: document, original: originalDocument } = parseDiffAsDocument(diff);

    super({
      document,
      originalDocument,
      filename,
      language,
      highlightedRanges,
      collapsedRanges,
    });

    this.diff = diff;
  }

  static createEmpty() {
    return new this({ filename: 'empty.txt', language: 'text' });
  }
}

export default [
  new DiffBasedExampleDocument({
    diff: [
      ' An example plain-text document 1',
      ' An example plain-text document 2',
      ' An example plain-text document 3',
      ' An example plain-text document 4',
      ' An example plain-text document 5',
      ' An example plain-text document 6',
      ' An example plain-text document 7',
      '+An example plain-text document NEW LINE',
      ' An example plain-text document 8',
      ' An example plain-text document 9',
      '+NEW LINE',
      ' An example plain-text document 10',
      ' An example plain-text document 11',
      ' An example plain-text document 12',
      ' An example plain-text document 13',
      ' An example plain-text document 14',
      ' An example plain-text document 15',
      ' An example plain-text document 16',
      ' An example plain-text document 17',
      ' An example plain-text document 18',
      ' An example plain-text document 19',
      ' An example plain-text document 20',
      ' An example plain-text document 21',
      '+An example plain-text document NEW LINE',
      ' An example plain-text document 22',
      ' An example plain-text document 23',
      ' An example plain-text document 24',
      '-An example plain-text document 25',
      '+An example plain-text MODIFIED document 25',
      ' An example plain-text document 26',
      ' An example plain-text document 27',
      ' An example plain-text document 28',
      ' An example plain-text document 29',
      ' An example plain-text document 30',
      ' An example plain-text document 31',
      ' An example plain-text document 32',
      ' An example plain-text document 33',
      ' An example plain-text document 34',
      ' An example plain-text document 35',
      ' ',
    ].join('\n'),
    filename: 'test.txt',
    language: 'text',
    highlightedRanges: [
      { startLine: 2, endLine: 4 },
      { startLine: 7, endLine: 10 },
      { startLine: 18, endLine: 20 },
    ],
    collapsedRanges: [
      { startLine: 1, endLine: 7 },
      { startLine: 12, endLine: 15 },
      { startLine: 25, endLine: 27 },
      { startLine: 30, endLine: 31 },
      { startLine: 38, endLine: 39 },
    ],
  }),

  new DiffBasedExampleDocument({
    diff: [
      ' // Some comment',
      ' ',
      ' var x = function functionName() { return `test ${abc}`; };',
      ' ',
      '-/**',
      '- *Completely irrelevant comment',
      '-*/',
      '-',
      '-const SOME_CONSTANT = "A very long constant. A very long constant. A very long constant. A very long constant. A very long constant. A very long constant. A very long constant. A very long constant. A very long constant. ";',
      '+const LONG_CONSTANT = "A very long constant. A very long constant. A very long constant. A very long constant. A very long constant. A very long constant. A very long constant. A very long constant. A very long constant. ";',
      ' ',
      ' /**',
      '- * Un-useful comment about method foo()',
      '- * More information about the method',
      '+ * Useful comment about method foo()',
      '+ * Six trailing whitespaces:      ',
      '+ * Two TAB charachers: \t\t ≪—— here',
      '+ * Three trailing TAB charachers: \t\t\t',
      '+ * Four invisible characters: ——≫ ‎‎‎‎ ≪—— here',
      ' */',
      ' ',
      ' async function foo() {',
      '   var x = 1;',
      '-  alert("123" + x);',
      "+  alert('123' + x);",
      ' }',
      ' ',
      ' async function fooTwo() {',
      '   var x = 1;',
      '-  alert("123" + x);',
      "+  alert('123' + x);",
      ' }',
      ' ',
      ' async function fooThree() {',
      '   var x = 1;',
      '-  alert("123" + x);',
      "+  alert('123' + x);",
      ' }',
      ' ',
    ].join('\n'),
    filename: 'test.js',
    language: 'javascript',
    highlightedRanges: [
      { startLine: 5, endLine: 5 },
      { startLine: 8, endLine: 12 },
      { startLine: 16, endLine: 17 },
      { startLine: 21, endLine: 22 },
      { startLine: 26, endLine: 27 },
    ],
    collapsedRanges: [
      { startLine: 7, endLine: 13 },
      { startLine: 20, endLine: 28 },
    ],
  }),

  new DiffBasedExampleDocument({
    diff: [
      '-<html><body>An example HTML document</body></html>',
      '+<html>',
      '+\t<head>',
      '+\t\t<link rel="icon" href="/assets/favicon.ico" />',
      '+\t</head>',
      '+\t<body class="main-body" onblur="function doSomething(param1, param2) { return [param + param2]; }">',
      '+\t\t<span aria-disabled style="height: 100px; color: greenyellow; font-weight: bold">',
      '+\t\t\t&nbsp;',
      '+\t\t</span>',
      '+\t\tAn example HTML document',
      '+\t\tWith an invisible  ——≫ ‎ ≪——  character',
      '+\t</body>',
      '+</html>',
    ].join('\n'),
    filename: 'test.html',
    language: 'html',
    highlightedRanges: [
      { startLine: 7, endLine: 7 },
      { startLine: 9, endLine: 9 },
    ],
    collapsedRanges: [
      { startLine: 2, endLine: 4 },
      { startLine: 6, endLine: 8 },
    ],
  }),

  new DiffBasedExampleDocument({
    diff: [
      ' # Assign a numeric value',
      ' number = 70',
      ' ',
      '-# Check the is more than 70 or not',
      '+# Check the value is more than 70 or not',
      ' if (number >= 70):',
      '-\tprint("They have passed")',
      '+\tprint("You have passed")',
      ' else:',
      '-\tprint("They have not passed")',
      '+\tprint("You have not passed")',
      ' ',
    ].join('\n'),
    filename: 'test.py',
    language: 'python',
    collapsedRanges: [
      { startLine: 1, endLine: 1 },
      { startLine: 3, endLine: 9 },
    ],
  }),

  new DiffBasedExampleDocument({
    diff: [
      ' -- Table: customer',
      ' CREATE TABLE customer (',
      ' \tid int  NOT NULL IDENTITY(1, 1),',
      ' \tcustomer_name varchar(255)  NOT NULL,',
      ' \tcity_id int  NOT NULL,',
      ' \tcustomer_address varchar(255)  NOT NULL,',
      ' \tnext_call_date date  NULL,',
      ' \tts_inserted datetime  NOT NULL,',
      ' \tCONSTRAINT customer_pk PRIMARY KEY  (id)',
      ' );',
      ' ',
      '--- Table: customer_backup',
      '-CREATE TABLE customer_backup (',
      '-\tid int  NOT NULL IDENTITY(1, 1),',
      '-\tcustomer_name varchar(255)  NOT NULL,',
      '-\tcity_id int  NOT NULL,',
      '-\tcustomer_address varchar(255)  NOT NULL,',
      '-\tnext_call_date date  NULL,',
      '-\tts_inserted datetime  NOT NULL,',
      '-\tCONSTRAINT customer_pk PRIMARY KEY  (id)',
      '-);',
      '-',
      ' -- Table: customer_backup3',
      ' CREATE TABLE customer_backup3 (',
      ' \tid int  NOT NULL IDENTITY(1, 1),',
      ' \tcustomer_name varchar(255)  NOT NULL,',
      ' \tcity_id int  NOT NULL,',
      ' \tcustomer_address varchar(255)  NOT NULL,',
      ' \tnext_call_date date  NULL,',
      ' \tts_inserted datetime  NOT NULL,',
      ' \tCONSTRAINT customer_pk PRIMARY KEY  (id)',
      ' );',
      ' ',
      '+-- Extra comment',
      '+-- Extra comment',
      '+-- Extra comment',
      '+-- Extra comment',
      '+-- Extra comment',
      '+',
      ' -- Unchanged comment',
      ' -- Unchanged comment',
      ' -- Unchanged comment',
      ' -- Unchanged comment',
      ' -- Unchanged comment',
      ' ',
      ' -- Unchanged comment',
      ' -- Unchanged comment',
      ' -- Unchanged comment',
      ' -- Unchanged comment',
      ' -- Unchanged comment',
      ' ',
    ].join('\n'),
    filename: 'test.sql',
    language: 'sql',
    highlightedRanges: [
      { startLine: 14, endLine: 20 },
      { startLine: 35, endLine: 39 },
    ],
    collapsedRanges: [
      { startLine: 15, endLine: 19 },
      { startLine: 24, endLine: 26 },
    ],
  }),

  new DiffBasedExampleDocument({
    diff: [
      '-### Example Markdown document',
      '+# Example Markdown document',
      '+### Another header',
      '+###### Another header',
      '+-----------',
      '+__Bold text__',
      '+_Italic text_',
      '+',
      '+This in a example document with code snippets',
      '+',
      '+```ts',
      "+function myTestFunction({ world = 'world' } : { world!: string } = {}) {",
      '+  return window.confirm(`Hello ${world}!`)',
      '+}',
      '+```',
      '+',
      '+```html',
      '+<body>',
      '+  <head></head>',
      '+</body>',
      '+```',
      ' ',
    ].join('\n'),
    filename: 'test.md',
    language: 'markdown',
    highlightedRanges: [
      { startLine: 12, endLine: 12 },
      { startLine: 18, endLine: 18 },
    ],
    collapsedRanges: [
      { startLine: 1, endLine: 6 },
      { startLine: 21, endLine: 21 },
    ],
  }),

  new DiffBasedExampleDocument({
    diff: [
      ' # syntax=docker/dockerfile:1.7-labs',
      ' FROM mcr.microsoft.com/dotnet/sdk:8.0-alpine',
      ' ',
      '+WORKDIR /app',
      '+',
      '+# .git & README.md are unique per-repository. We ignore them on first copy to prevent cache misses',
      '+COPY --exclude=.git --exclude=README.md . /app',
      '+',
      '+# This saves nuget packages to ~/.nuget',
      '+RUN dotnet build --configuration Release .',
      '+',
      '+# This seems to cause a caching issue with the dotnet build command, where old contents are used',
      '+# TODO: See if this needs to be brought back?',
      '+# RUN rm -rf /app/obj',
      '+# RUN rm -rf /app/bin',
      '+',
      '+RUN echo "cd \\${CODECRAFTERS_SUBMISSION_DIR} && dotnet build --configuration Release ." > /codecrafters-precompile.sh',
      '+RUN chmod +x /codecrafters-precompile.sh',
      '+',
      '+ENV CODECRAFTERS_DEPENDENCY_FILE_PATHS="codecrafters-redis.csproj,codecrafters-redis.sln"',
      '+',
      '+# Once the heavy steps are done, we can copy all files back',
      '+COPY . /app',
      '+',
      ' ',
    ].join('\n'),
    filename: 'Dockerfile',
    language: 'dockerfile',
    highlightedRanges: [
      { startLine: 7, endLine: 7 },
      { startLine: 17, endLine: 18 },
    ],
    collapsedRanges: [
      { startLine: 12, endLine: 15 },
      { startLine: 22, endLine: 23 },
    ],
  }),

  new DiffBasedExampleDocument({
    diff: [
      ' ---',
      ' completion_percentage: 30',
      ' concept_slugs:',
      ' - network-protocols',
      ' - tcp-overview',
      ' description_md: |-',
      '   Redis is an in-memory data structure store often used as a database, cache, message broker and streaming engine. In this challenge',
      "   you'll build your own Redis server that is capable of serving basic commands, reading RDB files and more.",
      ' ',
      "   Along the way, you'll learn about TCP servers, the Redis Protocol and more.",
      ' extensions:',
      ' - description_markdown: |-',
      "     In this challenge extension you'll add support for [Lists][redis-lists] to your Redis implementation.",
      ' ',
      "     Along the way, you'll learn commands like [RPUSH][rpush-command], [LRANGE][lrange-command], and more.",
      ' ',
      '     [redis-lists]: https://redis.io/docs/latest/develop/data-types/lists/',
      '     [rpush-command]: https://redis.io/docs/latest/commands/rpush/',
      '     [lrange-command]: https://redis.io/docs/latest/commands/lrange/',
      '   name: Lists',
      '   slug: lists',
      ' - description_markdown: |-',
      "     In this challenge extension you'll add support for the [Stream][redis-streams-data-type] data type to your Redis implementation.",
      ' ',
      "     Along the way you'll learn about commands like [XADD][xadd-command], [XRANGE][xrange-command] and more.",
      ' ',
      '     [redis-streams-data-type]: https://redis.io/docs/data-types/streams/',
      '     [xadd-command]: https://redis.io/commands/xadd/',
      '     [xrange-command]: https://redis.io/commands/xrange/',
      '   name: Streams',
      '   slug: streams',
      ' - description_markdown: |-',
      "     In this challenge extension you'll add support for [Transactions][redis-transactions] to your Redis implementation.",
      ' ',
      "     Along the way, you'll learn about the [MULTI][multi-command], [EXEC][exec-command], and [DISCARD][discard-command] commands, as well as how Redis handles transactions atomically.",
      ' ',
      '     [redis-transactions]: https://redis.io/docs/latest/develop/interact/transactions/',
      '     [multi-command]: https://redis.io/commands/multi/',
      '     [exec-command]: https://redis.io/commands/exec/',
      '     [discard-command]: https://redis.io/commands/discard/',
      '   name: Transactions',
      '   slug: transactions',
      ' - description_markdown: |',
      "     In this challenge extension you'll add support for [Replication][redis-replication] to your Redis implementation.",
      ' ',
      "     Along the way you'll learn about how Redis's leader-follower replication works, the [PSYNC][redis-psync-command] command and more.",
      ' ',
      '     [redis-replication]: https://redis.io/docs/management/replication/',
      '     [redis-psync-command]: https://redis.io/commands/psync/',
      '   name: Replication',
      '   slug: replication',
      ' - description_markdown: |',
      "     In this challenge extension you'll add [persistence][redis-persistence] support to your Redis implementation.",
      ' ',
      "     Along the way you'll learn about Redis's [RDB file format][rdb-file-format] and more.",
      ' ',
      '     [redis-persistence]: https://redis.io/docs/manual/persistence/',
      '     [rdb-file-format]: https://github.com/sripathikrishnan/redis-rdb-tools/blob/548b11ec3c81a603f5b321228d07a61a0b940159/docs/RDB_File_Format.textile',
      '   name: RDB Persistence',
      '   slug: persistence-rdb',
      '+- description_markdown: |-',
      "+    In this challenge extension you'll add support for [Publish/Subscribe (Pub/Sub)][redis-pub-sub] to your Redis implementation.",
      '+',
      "+    Along the way, you'll learn commands like [SUBSCRIBE][subscribe-command], [PUBLISH][publish-command], and more.",
      '+',
      '+    [redis-pub-sub]: https://redis.io/docs/latest/develop/pubsub/',
      '+    [subscribe-command]: https://redis.io/docs/latest/commands/subscribe/',
      '+    [publish-command]: https://redis.io/docs/latest/commands/publish/',
      '+  name: Pub/Sub',
      '+  slug: pub-sub',
      ' languages:',
      ' - slug: c',
      ' - slug: clojure',
      ' - slug: cpp',
      ' - slug: crystal',
      ' - slug: csharp',
      ' - slug: elixir',
      ' - slug: gleam',
      ' - slug: go',
      ' - slug: haskell',
      ' - slug: java',
      ' - slug: javascript',
      ' - slug: kotlin',
      ' - slug: ocaml',
      ' - slug: odin',
      ' - slug: php',
      ' - slug: python',
      ' - slug: ruby',
      ' - slug: rust',
      ' - slug: scala',
      ' - alpha_tester_usernames:',
      '   - JWShroyer',
      '   release_status: alpha',
      '   slug: swift',
      ' - slug: typescript',
      ' - slug: zig',
      '',
    ].join('\n'),
    filename: 'test.yml',
    language: 'YAML',
    highlightedRanges: [
      { startLine: 7, endLine: 7 },
      { startLine: 17, endLine: 18 },
    ],
    collapsedRanges: [
      { startLine: 12, endLine: 15 },
      { startLine: 22, endLine: 23 },
    ],
  }),
];
